import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SupportButton from "@/components/base/SupportButton";
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  Wrench,
  Server,
  CalendarDays,
  Clock,
} from "lucide-react";
import { API_URL } from "@/lib/api";

interface HealthData {
  status: string;
  checks: Record<string, string>;
}

interface MaintenanceWindow {
  ambiente: string;
  horaInicio: string;
  horaFin: string;
  dias: string[];
}

interface NormalizedWindow {
  id?: number;
  date_start: string;
  date_end: string;
  environment: string;
  state: string;
  recommend_contingency?: boolean;
  horaInicio?: string;
  horaFin?: string;
  affects_prod?: boolean;
  affects_cert?: boolean;
  affects_precert?: boolean;
}

interface EnvImpact {
  environment: string;
  in_maintenance?: boolean;
  live_available?: boolean | null;
  unplanned_outage?: boolean;
  recommend_contingency?: boolean;
  reason?: string | null;
  active_window?: NormalizedWindow | null;
  next_window?: NormalizedWindow | null;
}

interface DgiiStatus {
  services: { servicio: string; estatus: string; ambiente: string }[];
  maintenance: {
    ventanaMantenimientos: MaintenanceWindow[];
    windows?: NormalizedWindow[];
    active?: NormalizedWindow[];
    upcoming?: NormalizedWindow[];
  };
  environments: Record<string, { estado: string } | null>;
  impact?: Record<string, EnvImpact>;
}

const SERVICE_MAP: Record<string, string> = {
  api: "API Gateway",
  postgres: "Base de datos",
  redis: "Cache/Colas",
};

const AMBIENTE_LABELS: Record<string, string> = {
  PreCertificacion: "Pre-Certificación",
  Produccion: "Producción",
  Certificacion: "Certificación",
};

export default function StatusPage() {
  const [darkMode, setDarkMode] = useState(false);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [dgii, setDgii] = useState<DgiiStatus | null>(null);
  const [dgiiLoading, setDgiiLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/health`)
      .then((r) => r.json())
      .then((data) => {
        setHealth(data);
        setHealthLoading(false);
      })
      .catch(() => setHealthLoading(false));
  }, []);

  useEffect(() => {
    const load = () =>
      fetch(`${API_URL}/dgii/status`)
        .then((r) => r.json())
        .then((data) => {
          setDgii(data);
          setDgiiLoading(false);
        })
        .catch(() => setDgiiLoading(false));
    load();
  }, []);

  const statusIcon = (status: string) => {
    if (status === "ok" || status === "healthy")
      return <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />;
    return <AlertCircle size={16} className="text-red-500 flex-shrink-0" />;
  };

  const statusBadge = (status: string) => {
    if (status === "ok" || status === "healthy")
      return <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full whitespace-nowrap">Operativo</span>;
    return <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-100 px-2.5 py-1 rounded-full whitespace-nowrap">Caído</span>;
  };

  const dgiiEnvBadge = (estado: string | null | undefined) => {
    if (estado === "Disponible")
      return <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full whitespace-nowrap">Disponible</span>;
    if (estado)
      return <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-100 px-2.5 py-1 rounded-full whitespace-nowrap">No Disponible</span>;
    return <span className="text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full whitespace-nowrap">Sin datos</span>;
  };

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className={`min-h-screen ${darkMode ? "bg-slate-950 text-white" : "bg-white text-slate-900"}`}>
        <Navbar darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)} />

        <main className="pt-24 pb-16 px-4 md:px-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 ${
              healthLoading ? "bg-slate-50 border border-slate-200"
              : health?.status === "ok" ? "bg-emerald-50 border border-emerald-100"
              : "bg-amber-50 border border-amber-100"
            }`}>
              {healthLoading ? (
                <Loader2 size={14} className="animate-spin text-slate-400" />
              ) : (
                <span className={`w-2 h-2 rounded-full ${
                  health?.status === "ok" ? "bg-emerald-500 animate-pulse-slow" : "bg-amber-500 animate-pulse-slow"
                }`} />
              )}
              <span className={`text-sm font-semibold ${
                healthLoading ? "text-slate-500"
                : health?.status === "ok" ? "text-emerald-700" : "text-amber-700"
              }`}>
                {healthLoading ? "Verificando..."
                  : health?.status === "ok" ? "Todos los sistemas operativos"
                  : "Algunos servicios degradados"}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Estado del servicio</h1>
            <p className="text-slate-500 text-sm">
              Última actualización: {new Date().toLocaleString("es-DO", { dateStyle: "full", timeStyle: "short" })}
            </p>
          </div>

          {/* Componentes emite.do */}
          <section className="mb-10">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-widest mb-4">Componentes</h2>
            <div className="rounded-2xl border border-slate-100 overflow-hidden">
              {healthLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-slate-300" size={24} />
                </div>
              ) : health ? (
                Object.entries(health.checks || {}).map(([key, status], i) => (
                  <div key={key} className={`flex items-center justify-between px-6 py-4 ${i > 0 ? "border-t border-slate-50" : ""} hover:bg-slate-50/50 transition-colors`}>
                    <div className="flex items-center gap-3">
                      {statusIcon(status)}
                      <span className="text-sm font-medium text-slate-800">{SERVICE_MAP[key] || key}</span>
                    </div>
                    {statusBadge(status)}
                  </div>
                ))
              ) : (
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-slate-400">No se pudo conectar con el servicio de estado.</p>
                </div>
              )}
            </div>
          </section>

          {/* DGII StatusECF */}
          <section className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Server size={16} className="text-blue-600" />
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-widest">DGII — Estado de servicios</h2>
            </div>

            {dgiiLoading ? (
              <div className="rounded-2xl border border-slate-100 p-8 flex items-center justify-center">
                <Loader2 className="animate-spin text-slate-300" size={20} />
              </div>
            ) : dgii?.environments ? (
              <div className="rounded-2xl border border-slate-100 overflow-hidden">
                {Object.entries(dgii.environments).map(([env, data], i) => (
                  <div key={env} className={`flex items-center justify-between px-6 py-4 ${i > 0 ? "border-t border-slate-50" : ""} hover:bg-slate-50/50 transition-colors`}>
                    <div className="flex items-center gap-3">
                      {data?.estado === "Disponible"
                        ? <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                        : <AlertCircle size={16} className="text-red-500 flex-shrink-0" />}
                      <span className="text-sm font-medium text-slate-800">DGII {env}</span>
                    </div>
                    {dgiiEnvBadge(data?.estado)}
                  </div>
                ))}

                {/* Detalle producción */}
                {dgii.services?.length > 0 && (
                  <div className="border-t border-slate-100">
                    <div className="px-6 py-3 bg-slate-50/50">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Servicios — Producción</span>
                    </div>
                    {dgii.services
                      .filter((s) => s.ambiente === "Produccion")
                      .map((svc, i) => (
                        <div key={svc.servicio} className={`flex items-center justify-between px-6 py-2.5 ${i > 0 ? "border-t border-slate-50" : ""}`}>
                          <span className="text-sm text-slate-600">{svc.servicio}</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            svc.estatus === "Disponible" ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-50"
                          }`}>{svc.estatus}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-100 p-6 text-center">
                <p className="text-sm text-slate-400">No se pudo obtener el estado de DGII.</p>
              </div>
            )}
          </section>

          {/* DGII Maintenance */}
          <section className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <CalendarDays size={16} className="text-amber-600" />
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-widest">DGII — Ventanas de mantenimiento programadas</h2>
            </div>

            {dgiiLoading ? (
              <div className="rounded-2xl border border-slate-100 p-8 flex items-center justify-center">
                <Loader2 className="animate-spin text-slate-300" size={20} />
              </div>
            ) : (
              <>
                {dgii?.impact && Object.values(dgii.impact).some((i) => i?.recommend_contingency) && (
                  <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
                    <p className="text-sm font-semibold text-amber-900">DGII recomienda emitir en contingencia</p>
                    <p className="text-sm text-amber-800 mt-1">
                      Hay una ventana activa o una caída no programada. Los envíos en línea pueden fallar hasta que el servicio vuelva.
                    </p>
                  </div>
                )}
                {(() => {
                  const normalized = (dgii?.maintenance?.windows || []).filter((w) => w.state !== "done");
                  if (normalized.length) {
                    return (
                      <div className="space-y-3">
                        {normalized.map((vm) => {
                          const start = new Date(vm.date_start);
                          const end = new Date(vm.date_end);
                          const active = vm.state === "in_progress";
                          return (
                            <div key={vm.id ?? `${vm.date_start}-${vm.environment}`} className="bg-white border border-slate-100 rounded-2xl p-5">
                              <div className="flex items-center justify-between gap-3 mb-3">
                                <div className="flex items-center gap-2">
                                  <Wrench size={16} className={active ? "text-red-500" : "text-amber-500"} />
                                  <h3 className="text-sm font-semibold text-slate-800">{vm.environment}</h3>
                                </div>
                                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                  active
                                    ? "text-red-700 bg-red-50 border border-red-100"
                                    : "text-amber-700 bg-amber-50 border border-amber-100"
                                }`}>
                                  {active ? "En curso" : "Programado"}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock size={14} className="text-slate-400" />
                                <span className="text-sm text-slate-600">
                                  {start.toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" })}
                                  {" — "}
                                  {end.toLocaleString("es-DO", { dateStyle: "medium", timeStyle: "short" })}
                                </span>
                              </div>
                              {vm.recommend_contingency ? (
                                <p className="text-xs text-amber-800 mt-2">Durante esta ventana hay que pasar a contingencia si se necesita emitir.</p>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  const grouped = dgii?.maintenance?.ventanaMantenimientos || [];
                  if (grouped.length) {
                    return (
                      <div className="space-y-4">
                        {grouped.map((vm) => (
                          <div key={`${vm.ambiente}-${vm.horaInicio}`} className="bg-white border border-slate-100 rounded-2xl p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <Wrench size={16} className="text-amber-500" />
                              <h3 className="text-sm font-semibold text-slate-800">{AMBIENTE_LABELS[vm.ambiente] || vm.ambiente}</h3>
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                              <Clock size={14} className="text-slate-400" />
                              <span className="text-sm text-slate-600">{vm.horaInicio} — {vm.horaFin}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {vm.dias.map((dia) => {
                                const [d, m, y] = dia.split("-").map(Number);
                                const dateObj = new Date(y, m - 1, d);
                                const isPast = dateObj < new Date();
                                return (
                                  <span key={dia} className={`text-xs px-2.5 py-1 rounded-lg border ${
                                    isPast ? "text-slate-400 bg-slate-50 border-slate-100 line-through"
                                    : "text-amber-700 bg-amber-50 border-amber-100 font-medium"
                                  }`}>
                                    {dateObj.toLocaleDateString("es-DO", { day: "numeric", month: "short", year: "numeric" })}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <div className="rounded-2xl border border-slate-100 p-6 text-center">
                      <p className="text-sm text-slate-400">No hay ventanas de mantenimiento programadas.</p>
                    </div>
                  );
                })()}
              </>
            )}
          </section>
        </main>

        <Footer />
        <SupportButton />
      </div>
    </div>
  );
}
